// Browsable log of IVR phone calls -- transcript only for now. The IVR
// dev's Supabase writer doesn't save a recording (audio) yet, just call
// metadata and the full transcript text, so there's deliberately no
// play/download-recording control here -- see the banner below.
import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RefreshCw, Info, PhoneOutgoing, UserCheck, Check, X } from "lucide-react";
import { endOfDay, format, startOfDay, startOfWeek } from "date-fns";
import { formatDateTime } from "@/lib/dates";
import {
  IvrCallLog,
  CallOutcome,
  OUTCOME_LABEL,
  classifyOutcome,
  callDurationSeconds,
  formatDuration,
  fetchIvrCallLogs,
  fetchCrmAccountsByPhone,
  fetchAccountIdsWithHomeAddress,
  normalizePhoneForMatch,
  LinkedCrmAccount,
} from "@/lib/ivrCallLogs";

const OUTCOME_BADGE: Record<CallOutcome, string> = {
  booked: "bg-green-900 text-green-300",
  transferred_booked: "bg-teal-900 text-teal-300",
  transferred_attempted: "bg-blue-900 text-blue-300",
  transferred_immediate: "bg-purple-900 text-purple-300",
  started_not_completed: "bg-yellow-900 text-yellow-300",
  abandoned: "bg-gray-700 text-gray-300",
};

type RangeMode = "today" | "week" | "custom" | "all";

const RANGE_LABEL: Record<RangeMode, string> = {
  today: "Today",
  week: "This Week",
  custom: "Date Range",
  all: "All Time",
};

const dateInputValue = (d: Date) => format(d, "yyyy-MM-dd");

// yyyy-MM-dd from a date input -> local Date (avoids new Date("yyyy-MM-dd")'s UTC parse).
const parseDateInput = (s: string): Date | null => {
  const [y, m, d] = s.split("-").map(Number);
  return y && m && d ? new Date(y, m - 1, d) : null;
};

const IvrCallLogTab: React.FC = () => {
  const [calls, setCalls] = useState<IvrCallLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [outcomeFilter, setOutcomeFilter] = useState<CallOutcome | "all">("all");
  const [rangeMode, setRangeMode] = useState<RangeMode>("today");
  const [fromDate, setFromDate] = useState(dateInputValue(new Date()));
  const [toDate, setToDate] = useState(dateInputValue(new Date()));
  const [selected, setSelected] = useState<IvrCallLog | null>(null);
  const [accountsByPhone, setAccountsByPhone] = useState<Map<string, LinkedCrmAccount[]>>(new Map());
  const [homeAddressAccountIds, setHomeAddressAccountIds] = useState<Set<string>>(new Set());

  const load = async () => {
    setLoading(true);
    const [callRows, accounts, homeIds] = await Promise.all([
      fetchIvrCallLogs(),
      fetchCrmAccountsByPhone(),
      fetchAccountIdsWithHomeAddress(),
    ]);
    setCalls(callRows);
    setAccountsByPhone(accounts);
    setHomeAddressAccountIds(homeIds);
    setLoading(false);
  };

  const accountsFor = (call: IvrCallLog): LinkedCrmAccount[] =>
    accountsByPhone.get(normalizePhoneForMatch(call.caller_phone)) || [];

  // Browser-local time (assumed NZ), same convention as the reporting tab.
  const rangeBounds = useMemo((): { start: Date; end: Date } | null => {
    const now = new Date();
    if (rangeMode === "today") return { start: startOfDay(now), end: endOfDay(now) };
    if (rangeMode === "week") return { start: startOfWeek(now, { weekStartsOn: 1 }), end: endOfDay(now) };
    if (rangeMode === "custom") {
      const from = parseDateInput(fromDate);
      const to = parseDateInput(toDate);
      if (!from || !to) return null;
      return from <= to ? { start: startOfDay(from), end: endOfDay(to) } : { start: startOfDay(to), end: endOfDay(from) };
    }
    return null;
  }, [rangeMode, fromDate, toDate]);

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return calls.filter((c) => {
      if (rangeBounds) {
        const t = new Date(c.started_at).getTime();
        if (t < rangeBounds.start.getTime() || t > rangeBounds.end.getTime()) return false;
      }
      if (outcomeFilter !== "all" && classifyOutcome(c) !== outcomeFilter) return false;
      if (!term) return true;
      return (
        (c.caller_phone || "").toLowerCase().includes(term) ||
        (c.transcript || "").toLowerCase().includes(term) ||
        (accountsByPhone.get(normalizePhoneForMatch(c.caller_phone)) || []).some(
          (a) => a.account_name.toLowerCase().includes(term) || a.account_id.toLowerCase().includes(term)
        )
      );
    });
  }, [calls, search, outcomeFilter, accountsByPhone, rangeBounds]);

  return (
    <Card className="shadow-lg bg-card border border-border">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-xl text-primary flex items-center gap-2">
          <PhoneOutgoing className="w-5 h-5" />
          Call Log
        </CardTitle>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </CardHeader>
      <CardContent>
        <div className="flex items-start gap-2 rounded-lg border border-blue-900/40 bg-blue-950/20 p-3 mb-4">
          <Info className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
          <p className="text-xs text-blue-200">
            Call recordings (audio) aren't saved to Supabase yet -- only call metadata and the full transcript. This
            will pick up a playback option automatically once the IVR dev adds a recording field.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-3">
          {(Object.keys(RANGE_LABEL) as RangeMode[]).map((mode) => (
            <Button
              key={mode}
              size="sm"
              variant={rangeMode === mode ? "default" : "outline"}
              onClick={() => setRangeMode(mode)}
            >
              {RANGE_LABEL[mode]}
            </Button>
          ))}
          {rangeMode === "custom" && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="dark-input h-8 w-[9.5rem] text-xs"
              />
              to
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="dark-input h-8 w-[9.5rem] text-xs"
              />
            </div>
          )}
          <span className="text-xs text-muted-foreground ml-auto">
            {filtered.length} call{filtered.length !== 1 ? "s" : ""}
          </span>
        </div>

        <div className="flex flex-wrap gap-3 mb-4">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search phone, CRM account or transcript..."
            className="max-w-xs"
          />
          <Select value={outcomeFilter} onValueChange={(v) => setOutcomeFilter(v as CallOutcome | "all")}>
            <SelectTrigger className="w-56">
              <SelectValue placeholder="All outcomes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Outcomes</SelectItem>
              {(Object.keys(OUTCOME_LABEL) as CallOutcome[]).map((o) => (
                <SelectItem key={o} value={o}>
                  {OUTCOME_LABEL[o]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground">No calls found.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Started</TableHead>
                  <TableHead>Caller Phone</TableHead>
                  <TableHead>CRM Account</TableHead>
                  <TableHead>Home Address</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Bookings</TableHead>
                  <TableHead>Outcome</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((call) => {
                  const outcome = classifyOutcome(call);
                  return (
                    <TableRow key={call.id} className="cursor-pointer hover:bg-yellow-900/10" onClick={() => setSelected(call)}>
                      <TableCell>{formatDateTime(call.started_at)}</TableCell>
                      <TableCell className="font-medium">{call.caller_phone || "--"}</TableCell>
                      <TableCell>
                        {(() => {
                          const linked = accountsFor(call);
                          if (linked.length === 0) return <span className="text-muted-foreground text-xs">Not linked</span>;
                          const first = linked[0];
                          return (
                            <span className="inline-flex items-center gap-1.5 text-sm">
                              <UserCheck className="w-4 h-4 text-green-400 shrink-0" />
                              <span className={first.is_active ? "" : "opacity-60"}>
                                {first.account_name} <span className="text-muted-foreground text-xs">#{first.account_id}</span>
                              </span>
                              {linked.length > 1 && <Badge className="bg-gray-700 text-gray-300">+{linked.length - 1}</Badge>}
                            </span>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const linked = accountsFor(call);
                          if (linked.length === 0) return <span className="text-muted-foreground text-xs">--</span>;
                          return linked.some((a) => homeAddressAccountIds.has(a.account_id)) ? (
                            <span title="Home address on file" className="inline-flex">
                              <Check className="w-5 h-5 text-green-500" aria-label="Home address on file" />
                            </span>
                          ) : (
                            <span title="No home address on file" className="inline-flex">
                              <X className="w-5 h-5 text-red-500" aria-label="No home address on file" />
                            </span>
                          );
                        })()}
                      </TableCell>
                      <TableCell>{formatDuration(callDurationSeconds(call))}</TableCell>
                      <TableCell>
                        {call.bookings_completed}/{call.bookings_started}
                      </TableCell>
                      <TableCell>
                        <Badge className={OUTCOME_BADGE[outcome]}>{OUTCOME_LABEL[outcome]}</Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-2xl">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle>{selected.caller_phone || "Unknown Caller"}</DialogTitle>
                <DialogDescription>
                  {formatDateTime(selected.started_at)} -- {formatDuration(callDurationSeconds(selected))}
                  {" -- "}
                  <Badge className={OUTCOME_BADGE[classifyOutcome(selected)]}>{OUTCOME_LABEL[classifyOutcome(selected)]}</Badge>
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className="col-span-2">
                    <span className="text-muted-foreground">CRM account</span>
                    {accountsFor(selected).length === 0 ? (
                      <p className="text-muted-foreground italic">Caller's number isn't on any CRM account.</p>
                    ) : (
                      accountsFor(selected).map((a) => (
                        <p key={a.account_id} className="text-foreground font-medium">
                          {a.account_name} <span className="text-muted-foreground">#{a.account_id}</span>
                          {!a.is_active && <span className="text-muted-foreground"> (inactive)</span>}
                        </p>
                      ))
                    )}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Bookings started</span>
                    <p className="text-foreground font-medium">{selected.bookings_started}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Bookings completed</span>
                    <p className="text-foreground font-medium">{selected.bookings_completed}</p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-muted-foreground">Call ID</span>
                    <p className="text-foreground font-mono text-xs break-all">{selected.call_id}</p>
                  </div>
                </div>

                {selected.transfer_reason && (
                  <div className="rounded-lg border border-blue-900/40 bg-blue-950/20 p-3">
                    <p className="text-xs font-semibold text-blue-300 uppercase mb-1">Transfer Reason</p>
                    <p className="text-sm text-blue-100">{selected.transfer_reason}</p>
                  </div>
                )}

                <div className="border-t border-border pt-3">
                  <p className="text-xs font-semibold text-yellow-500 uppercase mb-2">Transcript</p>
                  {selected.transcript ? (
                    <div className="rounded-lg border border-border bg-background/40 p-3 space-y-2">
                      {selected.transcript.split("\n").filter(Boolean).map((line, i) => {
                        const isAgent = line.startsWith("Agent:");
                        const isUser = line.startsWith("User:");
                        return (
                          <p
                            key={i}
                            className={`text-sm ${isAgent ? "text-blue-200" : isUser ? "text-foreground" : "text-muted-foreground italic"}`}
                          >
                            {line}
                          </p>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground italic">No transcript recorded for this call.</p>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default IvrCallLogTab;
